import { Button, Skeleton } from "@mui/material";
import { useEffect, type FunctionComponent } from "react";
import { Controller, useForm, type Path, type SubmitHandler } from "react-hook-form";
import ItemConfigNotificaciones, { TipoActividadConfigNotificaciones } from "./ItemConfigNotificaciones";
import { useActualizarMultiplesPreferencias, useObtenerPreferencias } from "@/shared/hooks/useConfiguracion.hook";
import customAlert from "../../CustomAlert";
import type { Preferencia } from "@/shared/types/interfaces/ApiCasos/Alertas.interface";
import { useTranslation } from "react-i18next";

interface NotificacionConfig {
    id: string;
    title: string;
    desc: string;
    habilitado: boolean;
    tipoActividad: TipoActividadConfigNotificaciones;
    hardColor: string;
    lightColor: string;
}

const NOTIFICACIONES_CONFIG: NotificacionConfig[] = [
    {
        id: 'archivoAgregado',
        title: 'Archivo Agregado',
        desc: 'Recibir notificaciones cuando se agregue un archivo',
        tipoActividad: TipoActividadConfigNotificaciones.DocumentoAgregado,
        habilitado: true,
        hardColor: 'sky-500',
        lightColor: 'sky-100',
    },
    {
        id: 'fijacionCreada',
        title: 'Fijación Creada',
        desc: 'Recibir notificaciones cuando se cree una fijación',
        tipoActividad: TipoActividadConfigNotificaciones.FijacionCreada,
        habilitado: true,
        hardColor: 'teal-500',
        lightColor: 'teal-100',
    },
    {
        id: 'audienciaCelebrada',
        title: 'Audiencia Celebrada',
        desc: 'Recibir notificaciones cuando se celebre una audiencia',
        tipoActividad: TipoActividadConfigNotificaciones.AudienciaCelebrada,
        habilitado: true,
        hardColor: 'emerald-500',
        lightColor: 'emerald-100',
    },
    {
        id: 'salaAsignada',
        title: 'Sala Asignada',
        desc: 'Recibir notificaciones cuando se asigne una sala',
        tipoActividad: TipoActividadConfigNotificaciones.SalaAsignada,
        habilitado: true,
        hardColor: 'green-500',
        lightColor: 'green-100',
    },
    {
        id: 'salaRevocada',
        title: 'Sala Revocada',
        desc: 'Recibir notificaciones cuando se revoque una sala',
        tipoActividad: TipoActividadConfigNotificaciones.SalaRevocada,
        habilitado: true,
        hardColor: 'red-500',
        lightColor: 'red-100',
    },
    {
        id: 'mensajeTribunal',
        title: 'Mensaje Tribunal',
        desc: 'Recibir notificaciones de mensajes del tribunal',
        tipoActividad: TipoActividadConfigNotificaciones.MensajeTribunal,
        habilitado: true,
        hardColor: 'yellow-500',
        lightColor: 'yellow-100',
    },
    {
        id: 'audienciaCancelada',
        title: 'Audiencia Cancelada',
        desc: 'Recibir avisos cuando se cancele una audiencia',
        tipoActividad: TipoActividadConfigNotificaciones.SalaRevocada,
        habilitado: true,
        hardColor: 'red-500',
        lightColor: 'red-100',
    },
];

type NotificacionesFormData = {
    preferencias: Record<string, { sistema: boolean; email: boolean }>;
};

interface NotificacionesConfigProps {
    onClose: () => void;
    idUsuario: number
    idDestinatario: number
    idUsuarioSesion: number
}

const NotificacionesConfig: FunctionComponent<NotificacionesConfigProps> = ({
    onClose, idUsuario, idDestinatario, idUsuarioSesion
}) => {

    const { t } = useTranslation();

    const {
        data: dataObtenerPreferencias,
        isPending: isPendingObtenerPreferencias,
        error: errorObtenerPreferencias,
        isFetching: isFetchingObtenerPreferencias
    } = useObtenerPreferencias({
        idDestinatario,
        idUsuario
    });

    const {
        mutate: mutateActualizarMultiplesPreferencias,
        isPending: isPendingActualizarMultiplesPreferencias
    } = useActualizarMultiplesPreferencias();

    const { control, handleSubmit, watch, reset, setValue } = useForm<NotificacionesFormData>({
        defaultValues: {
            preferencias: {}
        },
    });

    useEffect(() => {
        if (!dataObtenerPreferencias?.Data) return;

        const prefs = dataObtenerPreferencias.Data.reduce((acc, item) => {
            acc[String(item.IdTipoActividad)] = {
            sistema: !!item.NotificacionSistema,
            email: !!item.NotificacionEmail,
            };
            return acc;
        }, {} as Record<string, { sistema: boolean; email: boolean }>);

        reset({ preferencias: prefs });
    }, [dataObtenerPreferencias, reset]);

    const handleSaveNotifications : SubmitHandler<NotificacionesFormData> = (data) => {
        const preferenciasArray: Preferencia[] = Object.entries(data.preferencias).map(
            ([id, pref]) => ({
                idTipoActividad: Number(id),
                notificacionSistema: pref.sistema,
                notificacionEmail: pref.email,
            })
        );

        /* console.log(data)
        console.log(data.preferencias)
        console.log(preferenciasArray) */
        
        customAlert({
            icon: "question",
            titulo: "¿Estás seguro(a) de actualizar tu configuración de notificaciones? ",
            mensaje: "Con esta actualización recibirás correos dependiendo lo que definas",
            botonCancelar: true,
            cerrarEsc: false,
            cerrarClicFuera: false,
            textoBotonAceptar: "Sí",
            colorBotonCancelar: '#d33',
            textoBotonCancelar: "No",
            callback: (res) =>  {
                if (res) {
                    mutateActualizarMultiplesPreferencias({
                        idUsuario,
                        idDestinatario,
                        idUsuarioSesion,
                        preferencias: preferenciasArray as Preferencia[]
                    }, {
                        onSuccess: (res) => {
                            console.log(res)
                            customAlert({
                                icon: 'success',
                                titulo: `Configuración de notificaciones actualizada con éxito!`,
                                tiempo: 8,
                                toast: true,
                            });
                        },
                        onError: (err) => {
                            customAlert({
                                icon: 'error',
                                titulo: err.message || `Ha ocurrido un error al actualizar la configuración de notificaciones`,
                                tiempo: 8,
                                toast: true,
                            });
                        }
                    });
                }          
            }
        });
    };

    return (
        <>
        <div className='mb-6 flex flex-col h-[23rem] max-h-[23rem] overflow-y-auto w-full justify-between'>
            <form className='flex flex-wrap gap-6'>
                {
                    isFetchingObtenerPreferencias
                    ? 
                    <>
                        <Skeleton height={100} width={410}/>
                        <Skeleton height={100} width={410}/>
                        <Skeleton height={100} width={410}/>
                        <Skeleton height={100} width={410}/>
                        <Skeleton height={100} width={410}/>
                        <Skeleton height={100} width={410}/>
                    </>
                    : 
                    dataObtenerPreferencias?.Data.map((item, index)=>(
                        <Controller
                            key={item.IdTipoActividad}
                            name={`preferencias.${String(item.IdTipoActividad)}` as any}
                            control={control}
                            defaultValue={/* valor inicial por si aún no hay value */
                                (dataObtenerPreferencias?.Data && dataObtenerPreferencias.Data[index])
                                ? {
                                    sistema: !!dataObtenerPreferencias.Data[index].NotificacionSistema,
                                    email: !!dataObtenerPreferencias.Data[index].NotificacionEmail
                                }
                                : { sistema: false, email: false }
                            }
                            disabled={isPendingActualizarMultiplesPreferencias}
                            render={({ field }) => (
                                <ItemConfigNotificaciones
                                    TipoActividad={item.IdTipoActividad}
                                    title={item.TipoActividad}
                                    desc={item.DescripcionAlertas}
                                    hardColor={NOTIFICACIONES_CONFIG[index]?.hardColor || 'sky-500'}
                                    lightColor={NOTIFICACIONES_CONFIG[index]?.lightColor || 'sky-100'}
                                    sistema={field.value?.sistema ?? false}
                                    email={field.value?.email ?? false}
                                    onChangeSistema={(v) =>
                                        field.onChange({ ...(field.value ?? { sistema: false, email: false }), sistema: v })
                                    }
                                    onChangeEmail={(v) =>
                                        field.onChange({ ...(field.value ?? { sistema: false, email: false }), email: v })
                                    }
                                    isFetching={isPendingActualizarMultiplesPreferencias}
                                />
                            )}
                        />
                    ))
                }
            </form>
        </div>
        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
            <Button
                variant="outlined"
                color='error'
                onClick={onClose}
                disabled={isPendingActualizarMultiplesPreferencias}
            >
                {t('configuracionPerfil.cancel')}
            </Button>
            <Button
                variant="contained"
                onClick={handleSubmit(handleSaveNotifications)}
                className="normal-case rounded-xl px-6 shadow-md"
                loading={isPendingActualizarMultiplesPreferencias}
                loadingPosition="end"
                disabled={isPendingActualizarMultiplesPreferencias}
            >
                {t('configuracionPerfil.saveChanges')}
            </Button>
        </div>
        </>
    );
}

export default NotificacionesConfig;